import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { Spinner } from '@/components/Elements';
import { DEFAULT_USER_CURRENCY } from '@/config/appConstants';
import { Currency, DiscountTypes, InvoicePreviewItemTypes, InvoicePreviewTypes } from '@/types';
import { findRawInvoicePreviewPrice, getPriceText, parseCurrency } from '@/utils/helpers';

export type InvoiceUpdateSummaryProps = {
  invoiceUpdateSummaryData: InvoicePreviewTypes | null;
  isLoading: boolean;
  className?: string;
};

export const InvoiceUpdateSummary = ({
  invoiceUpdateSummaryData,
  isLoading = false,
  className,
}: InvoiceUpdateSummaryProps) => {
  const { t, i18n } = useTranslation();
  const prefix = 'Coupon.';

  const getItemListStyle = () => 'flex justify-between items-center';

  const getItemLabelStyle = () => 'mt-0';

  const findOldPrice = (data: InvoicePreviewTypes | null, language: string) => {
    if (!data || !data.items) {
      return null;
    }

    const price: InvoicePreviewItemTypes[] = data.items.filter((item: any) => item.amount < 0);

    if (!price[0]) {
      return getPriceText(0, data.currency, language);
    }

    return getPriceText((price[0].amount as number) * -1, price[0].currency, language);
  };

  const findRawEndingBalance = (data: InvoicePreviewTypes | null) => {
    if (!data) {
      return 0;
    }

    return data.endingBalance || 0;
  };

  const findRawStartingBalance = (data: InvoicePreviewTypes | null) => {
    if (!data) {
      return 0;
    }

    return data.startingBalance || 0;
  };

  const findStartingBalance = (data: InvoicePreviewTypes | null, language: string) => {
    if (!data) {
      return 0;
    }

    if (!data.startingBalance) {
      return getPriceText(0, data.currency, language);
    }

    const startingBalance = Math.abs(data.startingBalance); // We need the absolute value, no negatives

    return getPriceText(startingBalance, data.currency, language);
  };

  const findEndingBalance = (data: InvoicePreviewTypes | null, language: string) => {
    if (!data) {
      return 0;
    }

    if (!data.endingBalance) {
      return getPriceText(0, data.currency, language);
    }

    const endingBalance = Math.abs(data.endingBalance); // We need the absolute value, no negatives

    return getPriceText(endingBalance, data.currency, language);
  };

  const findNewPrice = (data: InvoicePreviewTypes | null, language: string) => {
    if (!data || !data.totalExcludingTax) {
      return null;
    }

    if ((data.totalExcludingTax as number) < 0) {
      return getPriceText(0, data.currency, language);
    }

    return getPriceText(data.totalExcludingTax, data.currency, language);
  };

  const findRawNewPrice = (data: InvoicePreviewTypes | null): number => {
    if (!data || !data.totalExcludingTax) {
      return 0;
    }

    return data.totalExcludingTax;
  };

  const findSubTotal = (data: InvoicePreviewTypes | null, language: string) => {
    if (!data) {
      return 0;
    }

    if (!data.totalExcludingTax || (data.totalExcludingTax as number) < 0) {
      return getPriceText(0, data.currency, language);
    }

    return getPriceText(data.totalExcludingTax, data.currency, language);
  };

  const findTax = (data: InvoicePreviewTypes | null, language: string) => {
    if (!data) {
      return 0;
    }

    if (!data.tax || (data.tax as number) < 0) {
      return getPriceText(0, data.currency, language);
    }

    return getPriceText(data.tax, data.currency, language);
  };

  const findTotal = (data: InvoicePreviewTypes | null, language: string) => {
    if (!data || !data.items) {
      return 0;
    }

    if ((data.amountDue as number) < 0) {
      return getPriceText(0, data.currency, language);
    }

    return getPriceText(data.amountDue, data.currency, language);
  };

  const findTotalBeforeBalance = (data: InvoicePreviewTypes | null, language: string) => {
    if (!data || !data.items) {
      return 0;
    }

    const newPrice = findRawNewPrice(invoiceUpdateSummaryData);

    const tax = data.tax as number;
    const totalBeforeBalance = newPrice + tax;

    return getPriceText(totalBeforeBalance, data.currency, language);
  };

  const getAmountOfDiscounts = (data: DiscountTypes, currency: Currency) => {
    const amountOf = data && data?.coupon.amountOff;

    if (!amountOf) {
      return null;
    }

    return parseCurrency(i18n.language, currency || DEFAULT_USER_CURRENCY, amountOf);
  };

  const isProrationCoupon = (data: DiscountTypes) => {
    return data && data?.coupon.metadata?.is_proration_coupon == 'true';
  };

  const getCouponName = (data: DiscountTypes): string | undefined => {
    if (data && data?.coupon.metadata?.description) {
      return data && generateCouponDescriptionTranslation(data?.coupon.metadata?.description);
    }
    if (isProrationCoupon(data)) {
      return t('InvoiceUpdateSummary.old');
    }

    return data && data?.coupon.name;
  };

  const generateCouponDescriptionTranslation = (text: string): string => {
    let _item = t(`${prefix}${text}`);
    _item.startsWith(prefix) && (_item = _item.replace(prefix, ''));
    return _item;
  };

  const getPercentOfDiscounts = (data: DiscountTypes) => {
    const percentOfDiscounts = data && data?.coupon.percentOff;

    return percentOfDiscounts
      ? `${percentOfDiscounts} ${t('InvoiceUpdateSummary.percentOf')}`
      : null;
  };

  const hasDiscounts = (data: InvoicePreviewTypes | null) => {
    return data?.discounts && data?.discounts.length > 0;
  };

  const getDiscountRow = (discount: DiscountTypes, currency: Currency, index: number) => {
    const amountOfDiscounts = getAmountOfDiscounts(discount, currency);
    const percentOfDiscounts = getPercentOfDiscounts(discount);
    const couponName = getCouponName(discount);

    if (isProrationCoupon(discount)) {
      return renderDiscountRowProrationCoupon(
        couponName,
        amountOfDiscounts,
        percentOfDiscounts,
        index
      );
    }

    if (amountOfDiscounts || percentOfDiscounts) {
      return (
        <div className="md__payments__summary--discount__row" key={index}>
          <p className={getItemLabelStyle()}>
            {t('Forms.Coupon.coupon')} {amountOfDiscounts || percentOfDiscounts}
            {t('Forms.Coupon.discount')}
          </p>
        </div>
      );
    }

    return null;
  };

  const getDiscountsRow = (data: InvoicePreviewTypes | null) => {
    const discounts = data?.discounts;

    if (!discounts) {
      return null;
    }

    return discounts.map((discount: DiscountTypes, index: number) => {
      return getDiscountRow(discount, data?.currency, index);
    });
  };

  const renderDiscountRowProrationCoupon = (
    couponName: string | undefined,
    amountOfDiscounts: any,
    percentOfDiscounts: any,
    index: number
  ) => {
    if (amountOfDiscounts || percentOfDiscounts) {
      return (
        <div key={index}>
          <div className={clsx(getItemListStyle())}>
            <p className={getItemLabelStyle()}>{couponName}</p>
            <p className={getItemLabelStyle()}>{amountOfDiscounts || percentOfDiscounts}</p>
          </div>
        </div>
      );
    }
    return null;
  };

  const newPrice = findNewPrice(invoiceUpdateSummaryData, i18n.language);
  const subtotal = findSubTotal(invoiceUpdateSummaryData, i18n.language);
  const tax = findTax(invoiceUpdateSummaryData, i18n.language);
  const total = findTotal(invoiceUpdateSummaryData, i18n.language);
  const hasDiscount = hasDiscounts(invoiceUpdateSummaryData);
  const oldPriceRaw = !!findRawInvoicePreviewPrice(invoiceUpdateSummaryData);
  const oldPrice = findOldPrice(invoiceUpdateSummaryData, i18n.language);
  const rawEndingBalance = findRawEndingBalance(invoiceUpdateSummaryData);
  const endingBalance = findEndingBalance(invoiceUpdateSummaryData, i18n.language);
  const rawStartingBalance = findRawStartingBalance(invoiceUpdateSummaryData);
  const startingBalance = findStartingBalance(invoiceUpdateSummaryData, i18n.language);
  const totalBeforeBalance = findTotalBeforeBalance(invoiceUpdateSummaryData, i18n.language);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[148px]">
        <Spinner size="xl" />
      </div>
    );
  }

  return (
    <div className={clsx(className ?? '', 'invoice__summary__wrapper')}>
      <div className="flex justify-between items-start">
        <p className={getItemLabelStyle()}>
          <span className="block">{t('InvoiceUpdateSummary.new')}</span>
          <span className="block">
            {hasDiscount && ` ${t('InvoiceUpdateSummary.withDiscount')} `}
          </span>
        </p>
        <p className={getItemLabelStyle()}>{newPrice}</p>
      </div>

      {oldPriceRaw && (
        <div className={clsx(getItemListStyle(), '!border-0')}>
          <p className={getItemLabelStyle()}>{t('InvoiceUpdateSummary.old')}</p>
          <p className={getItemLabelStyle()}>-{oldPrice}</p>
        </div>
      )}

      <div className={clsx(getItemListStyle(), 'row__separator')}>
        <p className={getItemLabelStyle()}>{t('InvoiceUpdateSummary.subtotal')}</p>
        <p className={getItemLabelStyle()}>{subtotal}</p>
      </div>

      {hasDiscount && getDiscountsRow(invoiceUpdateSummaryData)}

      <div className={clsx(getItemListStyle())}>
        <p className={getItemLabelStyle()}>{t('InvoiceUpdateSummary.tax')}</p>
        <p className={getItemLabelStyle()}>{tax}</p>
      </div>

      <div className={clsx(getItemListStyle(), 'row__separator')}>
        <p className={getItemLabelStyle()}>{t('InvoiceUpdateSummary.totalBeforeBalance')}</p>
        <p className={getItemLabelStyle()}>{totalBeforeBalance}</p>
      </div>

      {rawStartingBalance !== 0 && (
        <div className={clsx(getItemListStyle())}>
          <p className={clsx(getItemLabelStyle(), 'color--successNormal')}>
            {t('InvoiceUpdateSummary.creditAvailable')}
          </p>
          <p className={clsx(getItemLabelStyle(), 'color--successNormal')}>{startingBalance}</p>
        </div>
      )}

      <div className={clsx(getItemListStyle(), 'total__amount row__separator')}>
        <p className={getItemLabelStyle()}>{t('InvoiceUpdateSummary.total')}</p>
        <p className={getItemLabelStyle()}>{total}</p>
      </div>

      {rawEndingBalance !== 0 && (
        <div className="row__silent">
          <p className={getItemLabelStyle()}>{t('InvoiceUpdateSummary.balanceRemains')}</p>
          <p className={getItemLabelStyle()}>{endingBalance}</p>
        </div>
      )}
    </div>
  );
};
